/**
 * Display Rules
 *
 * shouldShow()     — synchronous device/URL/frequency gate
 * waitForTrigger() — async, resolves when the configured trigger fires
 * markShown()      — write frequency state to localStorage/sessionStorage
 */

// ============================================================================
// Types (mirrored from @segmentflow/api-schemas DisplayRulesSchema)
// NOTE: Defined locally to avoid importing from api-schemas in the CDN bundle.
// Shape must stay aligned with display-rules.schema.ts.
// ============================================================================

export interface DisplayRules {
  trigger?: "immediate" | "delay" | "scroll" | "exit_intent" | "click";
  delaySeconds?: number;
  scrollPercentage?: number;
  clickSelector?: string;
  frequency?: "every_visit" | "once" | "once_per_session" | "custom";
  frequencyDays?: number;
  showOnMobile?: boolean;
  showOnDesktop?: boolean;
  urlPatterns?: string[];
  excludeUrlPatterns?: string[];
  showCloseButton?: boolean;
}

// ============================================================================
// Helpers
// ============================================================================

const storageKey = (formId: string) => `sf_form_shown_${formId}`;

// ============================================================================
// shouldShow
// ============================================================================

/**
 * Synchronous gate — returns false if the form should NOT be shown based on
 * device type, URL targeting, or frequency capping.
 */
export function shouldShow(rules: DisplayRules, formId: string): boolean {
  const showOnMobile = rules.showOnMobile ?? true;
  const showOnDesktop = rules.showOnDesktop ?? true;

  // Device check
  const isMobile = window.innerWidth < 768;
  if (isMobile && !showOnMobile) return false;
  if (!isMobile && !showOnDesktop) return false;

  // URL include patterns — if any are set, current URL must match at least one
  const url = window.location.href;
  if (rules.urlPatterns?.length) {
    const matched = rules.urlPatterns.some((p) => {
      try {
        return new RegExp(p).test(url);
      } catch {
        return false;
      }
    });
    if (!matched) return false;
  }

  // URL exclude patterns — if current URL matches any, skip
  if (rules.excludeUrlPatterns?.length) {
    const excluded = rules.excludeUrlPatterns.some((p) => {
      try {
        return new RegExp(p).test(url);
      } catch {
        return false;
      }
    });
    if (excluded) return false;
  }

  // Frequency capping
  const frequency = rules.frequency ?? "once";
  const key = storageKey(formId);

  switch (frequency) {
    case "once": {
      if (localStorage.getItem(key)) return false;
      break;
    }
    case "once_per_session": {
      if (sessionStorage.getItem(key)) return false;
      break;
    }
    case "custom": {
      const ts = localStorage.getItem(key);
      if (ts) {
        const daysSince = (Date.now() - Number(ts)) / 86_400_000;
        if (daysSince < (rules.frequencyDays ?? 30)) return false;
      }
      break;
    }
    case "every_visit":
    default:
      break;
  }

  return true;
}

// ============================================================================
// markShown
// ============================================================================

/**
 * Record that this form has been shown, for frequency capping.
 */
export function markShown(
  formId: string,
  frequency: DisplayRules["frequency"],
): void {
  const key = storageKey(formId);
  switch (frequency ?? "once") {
    case "once":
      localStorage.setItem(key, "1");
      break;
    case "once_per_session":
      sessionStorage.setItem(key, "1");
      break;
    case "custom":
      localStorage.setItem(key, String(Date.now()));
      break;
    case "every_visit":
    default:
      // No state written — always show
      break;
  }
}

// ============================================================================
// waitForTrigger
// ============================================================================

/**
 * Returns a Promise that resolves when the configured trigger fires.
 *
 * - immediate   → resolves immediately
 * - delay       → resolves after delaySeconds
 * - scroll      → resolves when user scrolls past scrollPercentage
 * - exit_intent → resolves when exit intent is detected (mouse near top
 *                  edge, Cmd/Ctrl+L) with grace period to avoid false positives
 * - click       → resolves when an element matching clickSelector is clicked
 */
export function waitForTrigger(rules: DisplayRules): Promise<void> {
  switch (rules.trigger ?? "immediate") {
    case "delay":
      return new Promise<void>((resolve) =>
        setTimeout(resolve, (rules.delaySeconds ?? 0) * 1000),
      );

    case "scroll": {
      const target = rules.scrollPercentage ?? 50;
      return new Promise<void>((resolve) => {
        const handler = () => {
          const scrollable =
            document.body.scrollHeight - window.innerHeight;
          if (scrollable <= 0) {
            window.removeEventListener("scroll", handler);
            resolve();
            return;
          }
          const scrolled = (window.scrollY / scrollable) * 100;
          if (scrolled >= target) {
            window.removeEventListener("scroll", handler);
            resolve();
          }
        };
        window.addEventListener("scroll", handler, { passive: true });
        // Check immediately in case the page is already scrolled enough
        handler();
      });
    }

    case "exit_intent": {
      // Improved exit intent detection modelled after Ouibounce / OptinMonster:
      //   1. Sensitivity threshold — fire when mouse is within SENSITIVITY px
      //      of the top edge, not only at clientY=0. This catches mouse
      //      movement toward the browser toolbar / back button area.
      //   2. Grace period — if the mouse re-enters the viewport within
      //      DELAY_MS, cancel the pending popup (prevents false positives
      //      when the user moves to a tab and comes back).
      //   3. Keyboard shortcut — Cmd+L / Ctrl+L (focus address bar) signals
      //      the user intends to navigate away.
      //   4. Minimum activation time — ignore exit signals during the first
      //      MIN_TIME_MS to avoid false fires on page load.
      const SENSITIVITY = 20; // px from top edge
      const DELAY_MS = 200; // ms grace period before showing
      const MIN_TIME_MS = 1_000; // ms before tracking starts

      return new Promise<void>((resolve) => {
        let delayTimer: ReturnType<typeof setTimeout> | null = null;
        const startTime = Date.now();

        const cleanup = () => {
          document.removeEventListener("mouseleave", mouseLeaveHandler);
          document.removeEventListener("mouseenter", mouseEnterHandler);
          window.removeEventListener("keydown", keydownHandler);
          if (delayTimer) {
            clearTimeout(delayTimer);
            delayTimer = null;
          }
        };

        const fire = () => {
          cleanup();
          resolve();
        };

        const mouseLeaveHandler = (e: MouseEvent) => {
          // Ignore events during the initial grace window
          if (Date.now() - startTime < MIN_TIME_MS) return;
          // Only fire when mouse leaves through the top of the viewport
          if (e.clientY > SENSITIVITY) return;

          // Start grace period — cancel if mouse re-enters
          delayTimer = setTimeout(fire, DELAY_MS);
        };

        const mouseEnterHandler = () => {
          if (delayTimer) {
            clearTimeout(delayTimer);
            delayTimer = null;
          }
        };

        const keydownHandler = (e: KeyboardEvent) => {
          if (Date.now() - startTime < MIN_TIME_MS) return;
          // Cmd+L (Mac) or Ctrl+L (Windows/Linux) = focus address bar
          if ((e.metaKey || e.ctrlKey) && e.key === "l") {
            fire();
          }
        };

        document.addEventListener("mouseleave", mouseLeaveHandler);
        document.addEventListener("mouseenter", mouseEnterHandler);
        window.addEventListener("keydown", keydownHandler);
      });
    }

    case "click": {
      const selector = rules.clickSelector ?? "";
      return new Promise<void>((resolve) => {
        const handler = (e: Event) => {
          if (!selector) {
            document.removeEventListener("click", handler);
            resolve();
            return;
          }
          const target = e.target as Node;
          const el = document.querySelector(selector);
          if (el && el.contains(target)) {
            document.removeEventListener("click", handler);
            resolve();
          }
        };
        document.addEventListener("click", handler);
      });
    }

    case "immediate":
    default:
      return Promise.resolve();
  }
}
